import { ReactNode, useMemo } from 'react';
import { useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import HandleOperation from 'components/utils/HandleOperation';
import { ArticleFormRelatedRecords, PostFormSkeleton } from '@buildwise/libs/posts';
import { ConfigEntryFieldType, ConfigEntryType } from 'config/config';
import { Box, Typography } from '@mui/material';
import { FieldWrapper } from '@buildwise/libs/people';
import { TaskFormData } from '../../../hooks/useAddTaskForm';
import { useFormContext } from 'react-hook-form';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { Asterisk, FieldLabel } from 'components/modals/addPostModal/PostDetailsForm';
import TaskTypeSelectField from './TaskTypeSelectField';
import TaskApplicableToSelectField from './TaskApplicableToSelectField';
import TaskImpactSelectField from './TaskImpactSelectField';
import TaskStatusSelectField from './TaskStatusSelectField';
import { FULL_WIDTH_TASK_FIELDS } from '@buildwise/libs/trackers/constants';

type TaskDetailsFormProps = {
  trackerFieldsConfigName: string;
  skeleton?: ReactNode;
  taskFields: ConfigEntryType[];
};

function TaskDetailsForm({ trackerFieldsConfigName, skeleton, taskFields }: TaskDetailsFormProps) {
  const { companyId } = useUserData();
  const {
    formState: { isSubmitting },
  } = useFormContext<TaskFormData>();
  const { isLoading: isLoadingTrackerConfigFields } = useConfigQuery({
    companyId,
    configName: trackerFieldsConfigName,
    shouldFetch: false,
  });

  const linkPostPlacementField = useMemo(() => {
    let placement = '';
    taskFields.some((field, index) => {
      if (field.entryCode === 'description') {
        placement = 'description';
        return true;
      }

      if (placement !== 'description' && field.entryCode === 'title') {
        placement = 'title';
        return index === taskFields.length - 1;
      }

      if (
        placement !== 'description' &&
        placement !== 'title' &&
        field.entryCode === 'attachments'
      ) {
        placement = 'attachments';
        return index === taskFields.length - 1;
      }
    });

    return placement;
  }, [taskFields]);
  const renderField = (field: ConfigEntryType, index: number, doNotAutoFocus?: boolean) => {
    switch (field.entryCode) {
      case 'title': {
        return (
          <>
            <RenderFilterFieldByType
              autoFocus={index === 0 && !doNotAutoFocus}
              field={field}
              showExternalLabel
            />
            {linkPostPlacementField === 'title' && (
              <ArticleFormRelatedRecords
                filters={[{ filterName: 'articleType', filterValues: ['SUGGESTION'] }]}
                title="Select Suggestions To Link"
              />
            )}
          </>
        );
      }

      case 'description': {
        return (
          <>
            <Box mb={0.5}>
              <FieldLabel id={field.entryCode} disabled={isSubmitting}>
                {field.referenceObject?.label || field.entryDescription}
                {field.referenceObject?.isRequired && <Asterisk>*</Asterisk>}
              </FieldLabel>
            </Box>
            <RenderFilterFieldByType
              autoFocus={index === 0 && !doNotAutoFocus}
              field={field}
              defaultHeight={200}
            />
            {linkPostPlacementField === 'description' && (
              <ArticleFormRelatedRecords
                filters={[{ filterName: 'articleType', filterValues: ['SUGGESTION'] }]}
                title="Select Suggestions To Link"
              />
            )}
          </>
        );
      }

      case 'type': {
        return <TaskTypeSelectField autoFocus={index === 0 && !doNotAutoFocus} field={field} />;
      }

      case 'impact': {
        return <TaskImpactSelectField autoFocus={index === 0 && !doNotAutoFocus} field={field} />;
      }

      case 'applicableTo': {
        return (
          <TaskApplicableToSelectField autoFocus={index === 0 && !doNotAutoFocus} field={field} />
        );
      }

      case 'status': {
        return <TaskStatusSelectField field={field} />;
      }

      default: {
        return <RenderFilterFieldByType autoFocus={index === 0 && !doNotAutoFocus} field={field} />;
      }
    }
  };

  const { defaultFields, groupedFields } = useMemo(() => {
    const defaultFields: ConfigEntryType[] = [];
    const groupedFields: Record<string, ConfigEntryType[]> = {};

    taskFields.forEach((field) => {
      if (!FULL_WIDTH_TASK_FIELDS.includes(field.entryCode)) {
        if (field.referenceObject?.groupEntryBy) {
          if (groupedFields[field.referenceObject.groupEntryBy]) {
            groupedFields[field.referenceObject.groupEntryBy].push(field);
          } else {
            groupedFields[field.referenceObject.groupEntryBy] = [field];
          }
        } else {
          if (groupedFields['Others']) {
            groupedFields['Others'].push(field);
          } else {
            groupedFields['Others'] = [field];
          }
        }
      } else {
        defaultFields.push(field);
      }
    });

    return {
      defaultFields,
      groupedFields: Object.fromEntries(
        Object.entries(groupedFields).sort(([keyA], [keyB]) => {
          if (keyA === 'Others') return 1;
          if (keyB === 'Others') return -1;
          return keyA.localeCompare(keyB);
        }),
      ),
    };
  }, [taskFields]);

  return (
    <>
      <HandleOperation
        isLoading={isLoadingTrackerConfigFields}
        skelton={skeleton || <PostFormSkeleton />}
        skeltonCount={1}
      >
        {defaultFields?.map((field, index) => {
          const _field = renderField(field, index);

          if (_field) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box key={field.entryCode} width={'100%'} mt={1}>
                  {FULL_WIDTH_TASK_FIELDS.includes(field.entryCode) ||
                  field.referenceObject?.type === ConfigEntryFieldType.EDITOR ? (
                    _field
                  ) : (
                    <FieldWrapper
                      id={field.entryCode}
                      label={field.referenceObject?.label || field.entryDescription}
                      required={field.referenceObject?.isRequired}
                      disabled={isSubmitting}
                    >
                      {_field}
                    </FieldWrapper>
                  )}
                </Box>
              </Box>
            );
          }
          return null;
        })}
        {/* Grouped SELECT & MULTI_SELECT fields */}
        <Box>
          {Object.keys(groupedFields)?.map((key) => {
            const fields = groupedFields[key];
            return (
              <>
                {Object.keys(groupedFields).length > 1 && (
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.custom.mistBlue,
                      fontSize: 14,
                      fontWeight: 700,
                      mt: 1,
                      mb: 0.5,
                    })}
                  >
                    {key}
                  </Typography>
                )}
                {fields.map((field, index) => {
                  const _field = renderField(field, index, true);

                  if (_field) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box key={field.entryCode} width={'100%'} mt={1}>
                          {FULL_WIDTH_TASK_FIELDS.includes(field.entryCode) ||
                          field.referenceObject?.type === ConfigEntryFieldType.EDITOR ? (
                            _field
                          ) : (
                            <FieldWrapper
                              id={field.entryCode}
                              label={field.referenceObject?.label || field.entryDescription}
                              required={field.referenceObject?.isRequired}
                              disabled={isSubmitting}
                            >
                              {_field}
                            </FieldWrapper>
                          )}
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })}
              </>
            );
          })}
        </Box>
      </HandleOperation>
    </>
  );
}

export default TaskDetailsForm;
